import React, { useContext, useState } from 'react'
import { CheckboxGroup } from '@strata-monorepo/shared-ui/components/CheckboxGroup/CheckboxGroup'
import { type FormStepProps, OdysseyContext } from '@redventures/odyssey'
import useFieldSelected from '@strata-monorepo/tracking/src/hooks/useFieldSelected'
import { useStoreState } from '../../store/useStore'

interface CheckboxOption {
  id: string
  label: string
  checked: boolean
}

interface CheckboxGroupComponentProps {
  formValue: string
  options: CheckboxOption[]
  tracking?: {
    stepName: string
    stepNumber: number
    fieldName: string
  }
}

const CheckboxQuestion: React.FC<FormStepProps> = ({ formStep }) => {
  const odysseyContext = useContext(OdysseyContext)
  const { componentProps } = formStep
  const { formValue, options = [], tracking } = componentProps as CheckboxGroupComponentProps

  const [allOptions, setAllOptions] = useState<CheckboxOption[]>(options)

  const trackFieldSelected = useFieldSelected()
  const formId = useStoreState((state) => state.form.formId)
  const formVersion = useStoreState((state) => state.form.formVersion)
  const currentStepIndex = useStoreState((state) => state.form.currentStepIndex)
  const formType = useStoreState((state) => state.form.formType)

  if (options.length === 0) {
    return null
  }

  const handleFieldSelected = (value: string) => {
    // Only track if tracking configuration is provided
    if (!tracking) {
      return
    }

    trackFieldSelected({
      formContext: {
        formName: formVersion,
        formType,
        formId,
      },
      stepContext: {
        stepName: tracking.stepName,
        stepId: String(tracking.stepNumber) || '0',
        stepNumber: currentStepIndex,
      },
      userInputField: {
        fieldName: tracking.fieldName,
        fieldType: 'checkbox',
        fieldValue: value || '',
        fieldSource: 'manual',
        autofilled: false,
      },
    })
  }

  const handleChange = (id: string, checked: boolean) => {
    const updatedOptions = allOptions.map((option) =>
      option.id === id ? { ...option, checked } : option,
    )
    // Keys of each of the selected options
    const selectedOptions = updatedOptions
      .filter((option) => option.checked)
      .map((option) => option.id)

    odysseyContext?.setValue(formValue, selectedOptions)
    handleFieldSelected(`[${selectedOptions.join(', ')}]`)
    setAllOptions(updatedOptions)
  }

  return (
    <div>
      <CheckboxGroup
        options={allOptions}
        onChange={handleChange}
        reverseOnMobile={true}
      />
      <p className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'>
        {odysseyContext?.getError(formValue)}
      </p>
    </div>
  )
}

export default CheckboxQuestion
